/**
 * <brick-cart-drawer>
 * Slide-in cart (<dialog>): focus trap, Esc/backdrop close, exit animation,
 * focus restored to the opener.
 *
 * - Opens on clicks on [data-cart-drawer-trigger] and on the
 *   `brick:cart:add` event. detail: { sections?, source? } — if
 *   `sections[sectionId]` is present it is rendered directly, otherwise the
 *   drawer re-fetches itself via the Section Rendering API.
 * - Intercepts submits of forms posting to /cart/add (opt out with
 *   [data-no-drawer]) and dispatches `brick:cart:add`.
 * - Quantity +/- , typed quantity and remove use /cart/change.js with
 *   `sections`, so one request returns the fresh drawer HTML.
 * - Order note saved with /cart/update.js (debounced, no re-render).
 * - After every render dispatches `brick:cart:updated`
 *   ({ itemCount, countText }) — the header updates its bubble.
 * Exposes window.BrickTheme.openCart(opener).
 */
(() => {
  if (customElements.get('brick-cart-drawer')) return;

  const theme = (window.BrickTheme = window.BrickTheme || {});
  const root = (theme.routes && theme.routes.root) || '/';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isPlainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const postJSON = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || '');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  class BrickCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.queue = Promise.resolve();

      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
      });

      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-cart-drawer-trigger]');
        if (!trigger || !isPlainClick(event)) return;
        event.preventDefault();
        this.open(trigger, { refresh: true });
      };
      this.onAdd = (event) => this.handleAdd(event.detail || {});
      this.onSubmit = (event) => this.interceptAdd(event);
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('brick:cart:add', this.onAdd);
      document.addEventListener('submit', this.onSubmit);

      const dialog = this.dialog;
      dialog.addEventListener('click', (event) => this.onDialogClick(event));
      dialog.addEventListener('change', (event) => this.onDialogChange(event));
      dialog.addEventListener('input', (event) => this.onDialogInput(event));
      dialog.addEventListener('keydown', (event) => this.onDialogKeydown(event));
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-closing');
        const target =
          this.opener && this.opener.isConnected ? this.opener : document.querySelector('[data-cart-drawer-trigger]');
        if (target) target.focus({ preventScroll: true });
      });

      theme.openCart = (opener) => this.open(opener, { refresh: true });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('brick:cart:add', this.onAdd);
      document.removeEventListener('submit', this.onSubmit);
    }

    get inner() {
      return this.querySelector('[data-cart-drawer-inner]');
    }

    /* ---------------------------------------------------------------- */
    /* Open / close                                                       */
    /* ---------------------------------------------------------------- */
    open(opener, { refresh = false } = {}) {
      if (opener instanceof HTMLElement) this.opener = opener;
      else if (!this.dialog.open) this.opener = document.activeElement;
      if (refresh) this.refresh();
      if (this.dialog.open) return;

      document.querySelectorAll('dialog[open]').forEach((dialog) => {
        if (dialog !== this.dialog) dialog.close();
      });
      this.dialog.showModal();
      const title = this.dialog.querySelector('#CartDrawer-Title');
      if (title) title.focus({ preventScroll: true });
    }

    close() {
      const dialog = this.dialog;
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (motionOff()) {
        dialog.close();
        return;
      }
      dialog.classList.add('is-closing');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.close();
      };
      dialog.querySelector('.drawer__panel').addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 400);
    }

    /* ---------------------------------------------------------------- */
    /* Events inside the drawer                                           */
    /* ---------------------------------------------------------------- */
    onDialogClick(event) {
      if (event.target === this.dialog || event.target.closest('[data-drawer-close]')) {
        this.close();
        return;
      }
      const line = event.target.closest('[data-line]');
      if (!line) return;

      const step = event.target.closest('[data-qty-step]');
      if (step) {
        const input = line.querySelector('[data-qty-input]');
        const increment = parseInt(input.step, 10) || 1;
        const direction = parseInt(step.dataset.qtyStep, 10);
        const quantity = Math.max(0, (parseInt(input.value, 10) || 0) + direction * increment);
        input.value = quantity;
        this.change(line.dataset.key, quantity, direction > 0 ? 'plus' : 'minus');
        return;
      }

      if (event.target.closest('[data-remove]')) this.change(line.dataset.key, 0, 'remove');
    }

    onDialogChange(event) {
      const input = event.target.closest('[data-qty-input]');
      if (!input) return;
      const line = input.closest('[data-line]');
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      this.change(line.dataset.key, quantity, 'input');
    }

    onDialogInput(event) {
      if (!event.target.matches('[data-cart-note]')) return;
      window.clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = window.setTimeout(() => {
        postJSON('cart/update.js', { note }).catch(() => {});
      }, 450);
    }

    onDialogKeydown(event) {
      if (event.key === 'Enter' && event.target.matches('[data-qty-input]')) {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active.id === 'CartDrawer-Title' || !this.dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- */
    /* Cart API                                                           */
    /* ---------------------------------------------------------------- */
    change(key, quantity, role) {
      this.queue = this.queue.then(() => this.runChange(key, quantity, role)).catch(() => {});
      return this.queue;
    }

    async runChange(key, quantity, role) {
      const inner = this.inner;
      const line = this.findLine(key);
      if (line) line.classList.add('is-loading');
      if (inner) inner.setAttribute('aria-busy', 'true');

      try {
        const cart = await postJSON('cart/change.js', {
          id: key,
          quantity,
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });
        const html = cart.sections && cart.sections[this.sectionId];
        if (!this.render(html, { key, role })) await this.refresh({ key, role });
      } catch (error) {
        const message = error.message || (inner && inner.dataset.errorText) || '';
        await this.refresh({ key, role });
        const fresh = this.findLine(key);
        const slot = fresh && fresh.querySelector('[data-line-error]');
        if (slot && message) {
          slot.textContent = message;
          slot.hidden = false;
        }
      }
    }

    async refresh(focus) {
      try {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        url.searchParams.set('_', String(Date.now()));
        // No `Accept: application/json` here — Shopify would answer some
        // routes with resource JSON instead of the sections payload.
        const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) return false;
        const data = await response.json();
        return this.render(data && data[this.sectionId], focus);
      } catch (error) {
        return false;
      }
    }

    handleAdd({ sections, source }) {
      const html = sections && sections[this.sectionId];
      if (!this.render(html)) this.refresh();
      this.open(source);
    }

    async interceptAdd(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
      if (form.hasAttribute('data-no-drawer')) return;
      let path = '';
      try {
        path = new URL(form.getAttribute('action') || '', window.location.href).pathname;
      } catch (e) {
        return;
      }
      if (!/\/cart\/add(\.js)?$/.test(path)) return;

      event.preventDefault();
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      if (submitter) {
        submitter.setAttribute('aria-disabled', 'true');
        submitter.setAttribute('aria-busy', 'true');
      }
      form.querySelectorAll('.cart-add-error').forEach((el) => el.remove());

      const body = new FormData(form);
      body.append('sections', this.sectionId);
      body.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw new Error(data.description || data.message || '');
        document.dispatchEvent(
          new CustomEvent('brick:cart:add', {
            detail: { item: data, sections: data.sections, source: submitter || form },
          })
        );
      } catch (error) {
        const message = error.message || (this.inner && this.inner.dataset.errorText) || '';
        const slot = document.createElement('p');
        slot.className = 'field__error cart-add-error';
        slot.setAttribute('role', 'alert');
        slot.textContent = message;
        form.append(slot);
      } finally {
        if (submitter) {
          submitter.removeAttribute('aria-disabled');
          submitter.removeAttribute('aria-busy');
        }
      }
    }

    /* ---------------------------------------------------------------- */
    /* Rendering                                                          */
    /* ---------------------------------------------------------------- */
    render(html, focus) {
      if (typeof html !== 'string') return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-drawer-inner]');
      const current = this.inner;
      if (!next || !current) return false;

      const hadFocus = current.contains(document.activeElement);
      const noteWasOpen = Boolean(current.querySelector('[data-preserve-open][open]'));
      const scrollTop = current.querySelector('.cart-drawer__body')?.scrollTop || 0;
      const oldFill = current.querySelector('[data-progress]');
      const oldProgress = oldFill ? oldFill.style.getPropertyValue('--progress') : '';

      const adopted = document.importNode(next, true);
      current.replaceWith(adopted);

      if (noteWasOpen) adopted.querySelector('[data-preserve-open]')?.setAttribute('open', '');
      const body = adopted.querySelector('.cart-drawer__body');
      if (body) body.scrollTop = scrollTop;

      // Animate the shipping bar from its previous value.
      const newFill = adopted.querySelector('[data-progress]');
      if (newFill && oldProgress) {
        const target = newFill.style.getPropertyValue('--progress');
        if (target !== oldProgress && !motionOff()) {
          newFill.style.setProperty('--progress', oldProgress);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => newFill.style.setProperty('--progress', target))
          );
        }
      }

      document.dispatchEvent(
        new CustomEvent('brick:cart:updated', {
          detail: {
            itemCount: parseInt(adopted.dataset.itemCount, 10) || 0,
            countText: adopted.dataset.countText || '',
          },
        })
      );

      if (this.dialog.open && (hadFocus || focus)) this.restoreFocus(focus);
      return true;
    }

    findLine(key) {
      if (!key) return null;
      return this.querySelector(`[data-line][data-key="${CSS.escape(key)}"]`);
    }

    restoreFocus(focus) {
      if (!focus) {
        this.querySelector('#CartDrawer-Title')?.focus({ preventScroll: true });
        return;
      }
      const lines = Array.from(this.querySelectorAll('[data-line]'));
      const line = focus && this.findLine(focus.key);
      const selectors = {
        plus: '[data-qty-step="1"]',
        minus: '[data-qty-step="-1"]',
        input: '[data-qty-input]',
        remove: '[data-remove]',
      };
      let target = null;
      if (line) {
        target = line.querySelector(selectors[focus.role] || '[data-qty-input]');
        if (target && target.disabled) target = line.querySelector('[data-qty-input]');
      } else if (lines.length) {
        target = lines[0].querySelector('[data-remove]');
      }
      if (!target) target = this.querySelector('#CartDrawer-Title');
      if (target) target.focus({ preventScroll: true });
    }
  }

  customElements.define('brick-cart-drawer', BrickCartDrawer);
})();
